import React, { Fragment, memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Label,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { useParams } from 'react-router-dom';

import ModalLayout from '../ModalLayout';
import {
  ReminderInstanceCalendarType,
  ReminderInstanceSignalType,
} from '../ProgressBars';
import {
  ALL_TYPE,
  CALENDAR_TYPE,
  SIGNAL_TYPE,
} from '../../Constants/GtemplateReminder';
import GatewayReminderInstanceService from '../../Services/GatewayReminderInstanceService';
import { truncateValueFromString } from '../../Lib/SignalUtils';

export default memo(UpdateMaintenanceServiceValuesModal);

const initState = {
  error: '',
  updateInfoMap: new Map(),
  requestPending: false,
};

function UpdateMaintenanceServiceValuesModal(props) {
  const service = useMemo(() => new GatewayReminderInstanceService(), []),
    { assetID } = useParams(),
    [state, updateState] = useState(() => {
      const updateInfoMap = Array.from(
        props.checkedInstances?.keys?.() ?? []
      ).reduce((map, gateway_reminder_instance_id) => {
        const { calendar_end, calendar_upcoming_epoch, signal_end } =
          props.instanceShadowValueMap?.get(gateway_reminder_instance_id) || {};
        return map.set(gateway_reminder_instance_id, {
          next_signal_value_due: signal_end,
          next_time_due: calendar_end,
          next_time_upcoming: calendar_upcoming_epoch,
        });
      }, new Map());
      return { ...initState, updateInfoMap };
    });

  let source;

  const handleUpdateServiceValuesClick = async () => {
    try {
      updateState((prevState) => ({ ...prevState, requestPending: true }));
      source = CancelToken.source();

      const body = Array.from(state.updateInfoMap ?? new Map()).map(
        ([gateway_reminder_instance_id, updates]) => ({
          gateway_reminder_instance_id,
          ...updates,
          next_time_due: updates.next_time_due
            ? new Date(updates.next_time_due).toISOString()
            : null,
          next_time_upcoming: updates.next_time_upcoming
            ? new Date(updates.next_time_upcoming).toISOString()
            : null,
        })
      );
      await service.updateGatewayReminderInstances(assetID, body, source.token);
      props.handleUpdateServiceValuesSuccess();
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        requestPending: false,
      }));
    }
  };

  const handleStatePropUpdate = ({ target: { name, value } }) => {
    const [prop, instance_id] = name.split(':');
    updateState((prevState) => {
      const instanceInfo = prevState.updateInfoMap.get(instance_id);
      const updateInfoMap = prevState.updateInfoMap.set(instance_id, {
        ...instanceInfo,
        [prop]: value,
      });
      return { ...prevState, updateInfoMap };
    });
  };

  useEffect(() => source?.cancel, [source]);

  return (
    <ModalLayout centered isOpen={props.isOpen} scrollable size="xl">
      <ModalHeader
        className="border-bottom-0 modal-title-text"
        toggle={props.toggleModal}
      >
        Update Reminder Service Values
        <span className="text-muted nine-tenth-text ml-3">
          {props.checkedInstances?.size ?? 0} selected
        </span>
      </ModalHeader>
      <ModalBody className="container-fluid pb-0">
        {state.error ? (
          <div className="row sticky-top bg-white">
            <p className="col-12 text-center text-danger nine-tenth-text">
              {state.error}
            </p>
          </div>
        ) : null}
        {Array.from(state.updateInfoMap ?? new Map()).map(
          ([instance_id, updates]) => {
            const instance = props.checkedInstances?.get?.(instance_id),
              { display_precision, unit } =
                props.signalUnitMap?.get(
                  instance.GtemplateReminder?.signal_id
                ) || {},
              {
                calendar_end,
                calendar_percentage,
                calendar_progress,
                calendar_text,
                calendar_upcoming,
                signal_current,
                signal_end,
                signal_margin,
                signal_percentage,
                signal_progress,
                signal_text,
              } = props.instanceShadowValueMap?.get(instance_id) || {},
              showCalendarInputs = [ALL_TYPE, CALENDAR_TYPE].includes(
                instance.GtemplateReminder.type
              ),
              showSignalInput = [ALL_TYPE, SIGNAL_TYPE].includes(
                instance.GtemplateReminder.type
              );

            return (
              <div
                className="row justify-content-center mb-3"
                key={instance_id}
              >
                <Card className="col-11">
                  <CardHeader className="bg-white">
                    <p className="mb-0 font-weight-bolder text-muted">
                      {instance?.GtemplateReminder?.name ?? ''}
                    </p>
                    <div className="w-75">
                      {(() => {
                        switch (instance.GtemplateReminder.type) {
                          case ALL_TYPE:
                            return (
                              <>
                                <div className="mt-2 mb-1">
                                  <ReminderInstanceCalendarType
                                    calendar_end={calendar_end}
                                    calendar_percentage={calendar_percentage}
                                    calendar_upcoming={calendar_upcoming}
                                    instance={instance}
                                    progress_color={calendar_progress}
                                    text={calendar_text}
                                  />
                                </div>
                                <div className="mt-1">
                                  <ReminderInstanceSignalType
                                    display_precision={display_precision}
                                    instance={instance}
                                    progress_color={signal_progress}
                                    signal_current={signal_current}
                                    signal_end={signal_end}
                                    signal_margin={signal_margin}
                                    signal_percentage={signal_percentage}
                                    text={signal_text}
                                    unit={unit}
                                  />
                                </div>
                              </>
                            );
                          case CALENDAR_TYPE:
                            return (
                              <ReminderInstanceCalendarType
                                calendar_end={calendar_end}
                                calendar_percentage={calendar_percentage}
                                calendar_upcoming={calendar_upcoming}
                                instance={instance}
                                progress_color={calendar_progress}
                                text={calendar_text}
                              />
                            );
                          case SIGNAL_TYPE:
                            return (
                              <ReminderInstanceSignalType
                                instance={instance}
                                progress_color={signal_progress}
                                signal_current={signal_current}
                                signal_end={signal_end}
                                signal_margin={signal_margin}
                                signal_percentage={signal_percentage}
                                text={signal_text}
                                unit={unit}
                              />
                            );
                          default:
                            return null;
                        }
                      })()}
                    </div>
                  </CardHeader>
                  <CardBody>
                    <div className="d-flex align-items-center justify-content-between">
                      {showSignalInput ? (
                        <div className="d-flex align-items-center">
                          <p className="mb-0 text-muted three-quarter-text text-nowrap mr-2">
                            Next Signal Value Due at:
                          </p>
                          <input
                            className="form-control form-control-sm rounded-lg d-inline"
                            name={`next_signal_value_due:${instance_id}`}
                            onChange={handleStatePropUpdate}
                            type="number"
                            value={truncateValueFromString(
                              updates.next_signal_value_due
                            )}
                          />
                          <p className="mb-0 ml-2 text-muted three-quarter-text text-nowrap">
                            {unit}
                          </p>
                        </div>
                      ) : null}
                      {showCalendarInputs ? (
                        <>
                          <FormGroup row className="align-items-center mb-0">
                            <Label className="mb-0 text-muted three-quarter-text text-nowrap mr-2">
                              Next Time Upcoming:
                            </Label>
                            <DatePicker
                              autoComplete="off"
                              className="form-control form-control-sm"
                              dateFormat={['MMM do, y']}
                              onChange={(selected) =>
                                handleStatePropUpdate({
                                  target: {
                                    name: `next_time_upcoming:${instance_id}`,
                                    value: selected,
                                  },
                                })
                              }
                              selected={new Date(updates.next_time_upcoming)}
                              showMonthDropdown
                              showYearDropdown
                              withPortal
                            />
                          </FormGroup>
                          <FormGroup row className="align-items-center mb-0">
                            <Label className="mb-0 text-muted three-quarter-text text-nowrap mr-2">
                              Next Time Due:
                            </Label>
                            <DatePicker
                              autoComplete="off"
                              className="form-control form-control-sm"
                              dateFormat={['MMM do, y']}
                              onChange={(selected) =>
                                handleStatePropUpdate({
                                  target: {
                                    name: `next_time_due:${instance_id}`,
                                    value: selected,
                                  },
                                })
                              }
                              selected={new Date(updates.next_time_due)}
                              showMonthDropdown
                              showYearDropdown
                              withPortal
                            />
                          </FormGroup>
                        </>
                      ) : null}
                    </div>
                  </CardBody>
                </Card>
              </div>
            );
          }
        ) ?? null}
      </ModalBody>
      <ModalFooter className="border-top-0">
        <Button
          className="px-4 py-0 nine-tenth-text"
          color="outline-secondary"
          disabled={state.requestPending}
          onClick={props.toggleModal}
        >
          Cancel
        </Button>
        <Button
          className="px-4 py-0 nine-tenth-text"
          color="success"
          disabled={state.requestPending}
          onClick={handleUpdateServiceValuesClick}
        >
          {state.requestPending ? (
            <FontAwesomeIcon icon={faCircleNotch} spin />
          ) : (
            'Update Values'
          )}
        </Button>
      </ModalFooter>
    </ModalLayout>
  );
}

UpdateMaintenanceServiceValuesModal.propTypes = {
  checkedInstances: PropTypes.object,
  handleUpdateServiceValuesSuccess: PropTypes.func,
  instanceShadowValueMap: PropTypes.object,
  isOpen: PropTypes.bool,
  signalUnitMap: PropTypes.object,
  toggleModal: PropTypes.func,
};
